/**
 * WidgetRenderer V2 - Rendu simplifié des widgets
 *
 * Switch simple sur le type de widget, passage direct des props sans transformation.
 * Gestion centralisée des états loading/empty.
 */

import React from "react";
import { Typography } from "@/components/ui";
import { FaExclamationCircle } from "react-icons/fa";
import type {
  WidgetConfig,
  WidgetData,
  ChartData,
  KpiData,
  TableData,
  DocumentsData,
  ChartVisualConfig,
  KpiVisualConfig,
  TextVisualConfig,
  WordCloudVisualConfig,
  TableVisualConfig,
  DocumentsVisualConfig,
  GraphVisualConfig,
  MapVisualConfig,
  GaugeVisualConfig,
} from "@/models/widget-v2";
import { FiltersContext } from "@/models/filter";
import { WIDGET_TYPES, WIDGET_REGISTRY } from "@/models/widget-v2";
import BaseChart, { ChartType } from "./charts/BaseChart";
import KpiCard from "./KpiCard";
import DataTable from "./DataTable";
import TextWidget from "./text-widget";
import GraphWidget from "./GraphWidget";
import MapWidget from "./MapWidget";
import DocumentsWidget from "./DocumentsWidget";
import WordCloudWidget from "./WordCloudWidget";

// ============================================================================
// Types
// ============================================================================

interface WidgetRendererProps {
  config: WidgetConfig;
  data: WidgetData;
  className?: string;
  filtersCtx?: FiltersContext;
  onTextChange?: (blocks: any[]) => void;
  onDataPointClick?: (label: string, value: number, dataIndex: number) => void;
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Vérifie si les données sont de type ChartData
 */
function isChartData(data: WidgetData): data is ChartData {
  if (!data) return false;
  return "labels" in data && "datasets" in data;
}

/**
 * Vérifie si les données sont de type KpiData
 */
function isKpiData(data: WidgetData): data is KpiData {
  if (!data) return false;
  return "value" in data && !("labels" in data);
}

/**
 * Vérifie si les données sont de type TableData
 */
function isTableData(data: WidgetData): data is TableData {
  if (!data) return false;
  return "columns" in data && "rows" in data;
}

/**
 * Vérifie si les données sont de type DocumentsData
 */
function isDocumentsData(data: WidgetData): data is DocumentsData {
  if (!data) return false;
  return "documents" in data && "total" in data;
}

/**
 * Vérifie si les données sont valides pour le widget Map
 * Accepte DocumentsData ou le format { type: string, data: MapDataPoint[] }
 */
function isMapData(data: WidgetData): boolean {
  if (!data) return false;
  // Format DocumentsData
  if ("documents" in data && "total" in data) return true;
  // Format { type: "world", data: [...] }
  if ("type" in data && "data" in data && Array.isArray((data as any).data))
    return true;
  return false;
}

/**
 * Mappe le type de widget V2 vers le type de graphique ApexCharts
 */
function mapToChartType(widgetType: string): ChartType {
  const mapping: Record<string, ChartType> = {
    [WIDGET_TYPES.LINE]: "line",
    [WIDGET_TYPES.BAR]: "bar",
    [WIDGET_TYPES.AREA]: "area",
    [WIDGET_TYPES.PIE]: "pie", // PIE et DONUT partagent le même type, donut utilise innerRadius > 0
    [WIDGET_TYPES.SCATTER]: "scatter",
    [WIDGET_TYPES.HEATMAP]: "heatmap",
    [WIDGET_TYPES.TREEMAP]: "treemap",
  };
  return mapping[widgetType] || "line";
}

// ============================================================================
// Error State
// ============================================================================

interface ErrorStateProps {
  message: string;
}

const ErrorState: React.FC<ErrorStateProps> = ({ message }) => (
  <div className="h-full w-full flex flex-col items-center justify-center text-error p-4">
    <FaExclamationCircle size={32} className="mb-3 opacity-50" />
    <Typography variant="text-sm" className="text-center">
      {message}
    </Typography>
  </div>
);

// ============================================================================
// Chart Widgets
// ============================================================================

interface ChartWidgetProps {
  type: ChartType;
  data: ChartData;
  visual: ChartVisualConfig;
  onDataPointClick?: (label: string, value: number, dataIndex: number) => void;
}

const ChartWidget: React.FC<ChartWidgetProps> = ({ type, data, visual, onDataPointClick }) => (
  <BaseChart type={type} data={data} visual={visual} onDataPointClick={onDataPointClick} />
);

// ============================================================================
// Main Component
// ============================================================================

export const WidgetRenderer: React.FC<WidgetRendererProps> = ({
  config,
  data,
  className = "",
  filtersCtx,
  onTextChange,
  onDataPointClick,
}) => {
  const { type, visual } = config;

  // Rendu selon le type de widget
  const renderWidget = () => {
    switch (type) {
      // ====== Charts temporels ======
      case WIDGET_TYPES.LINE:
      case WIDGET_TYPES.BAR:
      case WIDGET_TYPES.AREA:
        if (!isChartData(data)) {
          return <ErrorState message="Données de graphique invalides" />;
        }
        return (
          <ChartWidget
            type={mapToChartType(type)}
            data={data}
            visual={visual as ChartVisualConfig}
            onDataPointClick={onDataPointClick}
          />
        );

      // ====== Charts catégoriels (PIE et DONUT partagent le même type) ======
      case WIDGET_TYPES.PIE:
        if (!isChartData(data)) {
          return <ErrorState message="Données de graphique invalides" />;
        }
        return (
          <ChartWidget
            type={mapToChartType(type)}
            data={data}
            visual={visual as ChartVisualConfig}
            onDataPointClick={onDataPointClick}
          />
        );

      // ====== Charts spéciaux ======
      case WIDGET_TYPES.SCATTER:
      case WIDGET_TYPES.HEATMAP:
      case WIDGET_TYPES.TREEMAP:
        if (!isChartData(data)) {
          return <ErrorState message="Données de graphique invalides" />;
        }
        return (
          <ChartWidget
            type={mapToChartType(type)}
            data={data}
            visual={visual as ChartVisualConfig}
          />
        );

      // ====== KPI ======
      case WIDGET_TYPES.KPI:
        if (!isKpiData(data)) {
          return <ErrorState message="Données KPI invalides" />;
        }
        return (
          <KpiCard
            data={data}
            visual={visual as KpiVisualConfig}
            isGauge={false}
          />
        );

      // ====== Gauge ======
      case WIDGET_TYPES.GAUGE:
        if (!isKpiData(data)) {
          return <ErrorState message="Données de jauge invalides" />;
        }
        return (
          <KpiCard
            data={data}
            visual={visual as GaugeVisualConfig}
            isGauge={true}
          />
        );

      // ====== Table ======
      case WIDGET_TYPES.TABLE:
        if (!isTableData(data)) {
          return <ErrorState message="Données de tableau invalides" />;
        }
        return <DataTable data={data} visual={visual as TableVisualConfig} />;

      // ====== Documents ======
      case WIDGET_TYPES.DOCUMENTS:
        if (!isDocumentsData(data)) {
          return <ErrorState message="Données de documents invalides" />;
        }
        return (
          <DocumentsWidget
            data={data}
            visual={visual as DocumentsVisualConfig}
          />
        );

      // ====== Word Cloud ======
      case WIDGET_TYPES.WORDCLOUD:
        if (!isChartData(data)) {
          return <ErrorState message="Données de nuage de mots invalides" />;
        }
        return (
          <WordCloudWidget
            data={data}
            visual={visual as WordCloudVisualConfig}
          />
        );

      // ====== Text ======
      case WIDGET_TYPES.TEXT:
        return (
          <TextWidget
            widgetName={config.name}
            config={visual as TextVisualConfig}
            filtersCtx={filtersCtx || ({} as FiltersContext)}
            onChange={onTextChange}
          />
        );

      // ====== Graph relationnel ======
      case WIDGET_TYPES.GRAPH:
        if (!isDocumentsData(data)) {
          return <ErrorState message="Données de graphe invalides" />;
        }
        return (
          <GraphWidget
            data={data}
            visual={visual as GraphVisualConfig}
            title={visual.title}
          />
        );

      // ====== Carte géographique ======
      case WIDGET_TYPES.MAP:
        if (!isMapData(data)) {
          return <ErrorState message="Données de carte invalides" />;
        }
        return (
          <MapWidget
            data={data as any}
            visual={visual as MapVisualConfig}
            title={visual.title}
          />
        );

      // ====== Type inconnu ======
      default:
        return <ErrorState message={`Type de widget inconnu: ${type}`} />;
    }
  };

  return <div className={`h-full w-full ${className}`}>{renderWidget()}</div>;
};

export default WidgetRenderer;
